<template>
  <div>
    <page-header
      title="客如云免押单"
      :crumbs="['SaaS 优惠合作', '客如云']"
      :subtitle="`共 ${pagination.total} 笔免押单`"
    />

    <!-- 筛选条 -->
    <!-- 状态 Tab：按租赁单状态分组（老辛 2026-10-10 要求）。
     运营关心「单子走到哪一步」，而非「芝麻评估结果如何」，故以 rentalStatus 分维度。 -->
    <el-tabs v-model="activeTab" class="status-tabs" @tab-change="handleTabChange">
      <el-tab-pane
        v-for="t in statusTabs"
        :key="t.value"
        :name="t.value"
      >
        <template #label>
          <span class="tab-label">
   {{ t.label }}
      <span v-if="t.badge > 0" class="tab-badge">{{ t.badge }}</span>
    </span>
        </template>
      </el-tab-pane>
    </el-tabs>

    <div class="filter-bar">
      <el-select
        v-model="query.creditStatus"
        placeholder="免押状态"
        clearable
        style="width: 150px"
        @change="handleSearch"
      >
        <el-option label="评估中" value="PENDING" />
        <el-option label="已免押" value="PASSED" />
        <el-option label="未通过" value="FAILED" />
      </el-select>

      <el-input
        v-model="query.keyword"
        placeholder="单号 / 门店 / 用户"
        clearable
        style="width: 220px"
        @keyup.enter="handleSearch"
        @clear="handleSearch"
      />

      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="handleReset">重置</el-button>

      <div class="filter-bar__spacer" />
      <el-button :loading="loading" @click="fetchList">刷新</el-button>
    </div>

    <DataTable
      :columns="columns"
      :data="list"
      :loading="loading"
      :total="pagination.total"
      :page="pagination.pageNum"
      :page-size="pagination.pageSize"
      row-key="rentalNo"
      :ops-width="90"
      empty-text="暂无免押单（需先导入渠道门店映射数据）"
      @update:page="handleCurrentChange"
      @update:page-size="handleSizeChange"
      @refresh="fetchList"
    >
      <!-- 用户：昵称可能很长（如「支付宝用户586303586」），单行会被截断，
   故昵称与支付宝 UID 分两行展示；UID 才是唯一标识，昵称可能重复 -->
      <template #cell-userName="{ row }">
        <div class="user-cell">
          <span class="user-cell__name">{{ row.userName || '-' }}</span>
          <span v-if="row.alipayUserId" class="user-cell__uid">{{ row.alipayUserId }}</span>
          <span v-else-if="row.userPhone" class="user-cell__uid">{{ row.userPhone }}</span>
        </div>
      </template>

      <template #cell-creditStatus="{ row }">
        <span class="tag" :class="creditStatusClass(row.creditStatus)">
          {{ creditStatusText(row.creditStatus) }}
        </span>
        <span v-if="row.failReason && row.failReason !== 'UNKNOWN'" class="sub-tag">
          {{ failReasonText(row.failReason) }}
        </span>
      </template>

      <template #cell-money="{ row }">
        <div class="money-cell">
          <span class="money-cell__rest">¥{{ row.restAmount }}</span>
          <span class="money-cell__sub">共 {{ row.authAmount }}，已扣 {{ row.paidAmount || 0 }}</span>
        </div>
      </template>

      <template #cell-rentalStatus="{ row }">
        <span class="tag" :class="rentalStatusClass(row.rentalStatus)">
          {{ row.rentalStatus || '-' }}
        </span>
      </template>

      <template #ops="{ row }">
        <!-- 列表只留「详情」。
             扣款等资金操作一律收进详情抽屉（老辛 2026-10-10）：
             列表是「浏览」场景，操作按钮铺在每行容易被误点；
             且资金操作要连带展示单据全貌与操作流水，放详情里更稳。 -->
     <button
          v-if="hasPermission(PERM.detail)"
          class="dt-op"
   @click="openDetail(row)"
      >详情</button>
        <span v-else class="dt-none">-</span>
      </template>
    </DataTable>

    <!-- ============ 详情抽屉 ============ -->
    <el-drawer v-model="detailVisible" title="免押单详情" size="720px">
      <div v-loading="detailLoading" class="detail">
        <template v-if="detail">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="业务单号">
              <span class="mono">{{ detail.rentalNo }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="门店">{{ detail.storeName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商品">{{ detail.productName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="用户">{{ detail.userName || '-' }}（{{ detail.userPhone || '无手机' }}）</el-descriptions-item>
            <el-descriptions-item label="免押状态">
              <span class="tag" :class="creditStatusClass(detail.creditStatus)">
                {{ creditStatusText(detail.creditStatus) }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="租赁单状态">
              <span class="tag" :class="rentalStatusClass(detail.rentalStatus)">
                {{ detail.rentalStatus || '-' }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="授权额度">¥{{ detail.authAmount }}</el-descriptions-item>
            <el-descriptions-item label="已扣金额">¥{{ detail.paidAmount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="剩余可扣">
              <span class="money-cell__rest">¥{{ detail.restAmount }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="授权单号">
              <span class="mono">{{ detail.authNo || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="结果来源">{{ detail.confirmSource || '-' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ formatDate(detail.createdTime) }}</el-descriptions-item>
            <el-descriptions-item label="确认时间">{{ formatDate(detail.confirmedAt) }}</el-descriptions-item>
            <el-descriptions-item v-if="detail.closeReason" label="关闭原因" :span="2">
              {{ detail.closeReason }}
            </el-descriptions-item>
          </el-descriptions>

   <!-- 资金操作区（老辛 2026-10-10：全部操作从列表移到这里）。
   每个按钮独立受权限 + 可操作性双重控制：
     权限 = 有没有资格做（sys 级角色配置）
     canPay/canUnfreeze = 这笔单当前能不能做（业务状态决定）
         两者都满足才显示，避免「有权限但点不动」的困惑。 -->
          <div class="detail__ops">
            <span class="detail__ops-label">资金操作</span>
            <template v-if="hasOpPermission">
              <el-button
                v-if="detail.canPay"
                type="danger"
                size="small"
                @click="openPay(detail, 'full')"
              >全额扣款</el-button>
              <el-button
                v-if="detail.canPay"
                type="warning"
                size="small"
                @click="openPay(detail, 'partial')"
              >部分扣款</el-button>
              <el-button
                v-if="detail.canUnfreeze"
                size="small"
                @click="openUnfreeze(detail)"
              >取消扣款（解冻）</el-button>
              <el-button
                size="small"
                @click="openCancel(detail)"
              >取消免押单</el-button>
              <span v-if="!detail.canPay && !detail.canUnfreeze" class="detail__ops-empty">
                该单当前状态不支持扣款操作
              </span>
            </template>
            <span v-else class="detail__ops-empty">无操作权限</span>
          </div>

          <div class="detail__section-title">资金操作流水</div>
          <el-table :data="detail.opLogs || []" size="small" border>
            <el-table-column prop="createdTime" label="时间" width="160" />
            <el-table-column prop="opTypeText" label="操作" width="100" />
            <el-table-column label="金额" width="100" align="right">
              <template #default="{ row }">¥{{ row.amount ?? 0 }}</template>
            </el-table-column>
            <el-table-column label="结果" width="90">
              <template #default="{ row }">
                <span class="tag" :class="row.result === 'SUCCESS' ? 'st-done' : 'st-warn'">
                  {{ row.result === 'SUCCESS' ? '成功' : '失败' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="tradeNo" label="交易号" min-width="180" show-overflow-tooltip />
            <el-table-column prop="operatorId" label="操作人" width="90" />
            <el-table-column label="备注/失败原因" min-width="160" show-overflow-tooltip>
              <template #default="{ row }">{{ row.remark || row.errorMessage || '-' }}</template>
            </el-table-column>

      <!-- 操作列：关闭交易 / 退款（老辛 2026-10-10）。
           这两个动作的对象是「某一笔扣款交易」，所以挂在流水行上，而非免押单上。 -->
     <el-table-column label="操作" width="150" fixed="right">
            <template #default="{ row }">
    <!-- 仅扣款类流水才有这两个操作 -->
              <template v-if="isPayOp(row.opType)">
    <button
                  v-if="row.result === 'FAILED' && !row.closed"
 class="dt-op is-danger"
        @click="openCloseTrade(row)"
  >关闭交易</button>
        <button
 v-if="row.canRefund"
           class="dt-op"
        @click="openRefund(row)"
    >退款</button>
     <span
 v-if="row.result === 'FAILED' && row.closed"
      class="dt-none"
          >已关闭</span>
   <span
   v-if="row.result === 'SUCCESS' && !row.canRefund && row.refunded > 0"
              class="dt-none"
           >已退完</span>
       </template>
       <span v-else class="dt-none">-</span>
         </template>
      </el-table-column>
          </el-table>
        </template>
      </div>
    </el-drawer>

    <!-- ============ 扣款对话框 ============ -->
    <el-dialog v-model="payVisible" :title="payMode === 'full' ? '全额扣款' : '部分扣款'" width="520px">
      <el-alert
        v-if="payRow"
        :title="`单号 ${payRow.rentalNo}`"
        type="warning"
        :closable="false"
        show-icon
        class="pay-alert"
      />
      <el-form label-position="top" class="pay-form">
        <el-form-item label="免押额度">
          <span class="money">¥{{ payRow?.authAmount }}</span>
        </el-form-item>
        <el-form-item label="剩余可扣">
          <span class="money">¥{{ payRow?.restAmount }}</span>
        </el-form-item>
        <el-form-item v-if="payMode === 'partial'" label="本次扣款金额">
          <el-input-number
            v-model="payForm.amount"
            :min="0.01"
            :max="Number(payRow?.restAmount || 0)"
            :precision="2"
            :step="10"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item v-if="payMode === 'full'" label="本次扣款金额">
          <span class="money">¥{{ payRow?.restAmount }}（全额）</span>
        </el-form-item>
        <el-form-item label="扣款原因" required>
          <el-input
            v-model="payForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请说明扣款原因，如：设备丢失赔偿 / 超期未归还"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="payVisible = false">取消</el-button>
        <el-button type="danger" :loading="paySubmitting" @click="submitPay">
          确认{{ payMode === 'full' ? '全额扣款' : '扣款' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- ============ 取消扣款 / 取消免押单 ============ -->
    <el-dialog v-model="unfreezeVisible" title="取消扣款（解冻资金）" width="520px">
      <el-alert
        v-if="unfreezeRow"
        :title="`将解冻剩余额度 ¥${unfreezeRow.restAmount}，资金原路退回用户`"
        type="warning"
        :closable="false"
        show-icon
        class="pay-alert"
      />
      <el-form label-position="top" class="pay-form">
        <el-form-item label="取消原因" required>
          <el-input
            v-model="unfreezeForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请说明取消原因，如：用户协商解除 / 设备已归还"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="unfreezeVisible = false">返回</el-button>
        <el-button type="danger" :loading="unfreezeSubmitting" @click="submitUnfreeze">
          确认取消扣款
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="cancelVisible" title="取消免押单" width="520px">
      <el-alert
        v-if="cancelRow"
        title="此操作将作废该免押单。已扣款金额不予退还，剩余冻结资金会先解冻退回用户。"
        type="error"
        :closable="false"
        show-icon
        class="pay-alert"
      />
      <el-form label-position="top" class="pay-form">
        <el-form-item label="业务单号">
          <span class="mono">{{ cancelRow?.rentalNo }}</span>
        </el-form-item>
        <el-form-item label="取消原因" required>
          <el-input
            v-model="cancelForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请说明取消原因"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="cancelVisible = false">返回</el-button>
        <el-button type="danger" :loading="cancelSubmitting" @click="submitCancel">
          确认取消免押单
        </el-button>
      </template>
    </el-dialog>

  <!-- ============ 关闭交易（扣款失败的交易） ============ -->
    <el-dialog v-model="closeTradeVisible" title="关闭交易" width="520px">
      <el-alert
        v-if="closeTradeRow"
        title="仅用于关闭「已发起但扣款失败、一直没扣到」的交易。已扣款成功的请用退款。"
   type="warning"
        :closable="false"
        show-icon
        class="pay-alert"
      />
      <el-form label-position="top" class="pay-form">
    <el-form-item label="交易号">
NaN
   </el-form-item>
    <el-form-item label="关闭原因" required>
    <el-input
            v-model="closeTradeForm.remark"
 type="textarea"
        :rows="3"
   placeholder="请说明关闭原因，如：金额算错 / 订单取消"
  />
 </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeTradeVisible = false">返回</el-button>
        <el-button type="danger" :loading="closeTradeSubmitting" @click="submitCloseTrade">
          确认关闭交易
   </el-button>
  </template>
    </el-dialog>

    <!-- ============ 退款（扣款成功的交易） ============ -->
    <el-dialog v-model="refundVisible" title="退款" width="520px">
      <el-alert
        v-if="refundRow"
        :title="`该笔已扣款 ¥${refundRow?.amount || 0}，可退 ¥${refundRow?.refundable || 0}。退款原因会展示在用户支付宝账单里`"
        type="warning"
        :closable="false"
   show-icon
 class="pay-alert"
   />
      <el-form label-position="top" class="pay-form">
  <el-form-item label="原扣款金额">
          <span class="money">¥{{ refundRow?.amount }}</span>
     </el-form-item>
        <el-form-item label="已退金额">
       <span class="money">¥{{ refundRow?.refunded || 0 }}</span>
        </el-form-item>
        <el-form-item label="可退金额">
   <span class="money">¥{{ refundRow?.refundable || 0 }}</span>
   </el-form-item>
     <el-form-item label="本次退款金额">
   <el-input-number
    v-model="refundForm.amount"
   :min="0.01"
            :max="Number(refundRow?.refundable || 0)"
            :precision="2"
      style="width: 100%"
/>
    <div class="form-tip">留空或填 0 表示全额退回剩余可退金额</div>
  </el-form-item>
        <el-form-item label="退款原因" required>
    <el-input
      v-model="refundForm.remark"
            type="textarea"
       :rows="3"
      placeholder="请说明退款原因，如：设备已归还 / 协商解除"
          />
        </el-form-item>
   </el-form>
      <template #footer>
     <el-button @click="refundVisible = false">返回</el-button>
        <el-button type="danger" :loading="refundSubmitting" @click="submitRefund">
     确认退款
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import saasChannelService from '@/services/saasChannelService'
import store from '@/store'

export default defineComponent({
  name: 'KeruyunCreditOrderList',
  components: { PageHeader, DataTable },
  setup() {
    // ===== 权限（沿用项目标准写法，见 StoreList.vue）=====
    // 权限码与 sys_permission.permission_code 一一对应
    const PERM = {
      detail: 'saas:keruyun:creditOrder',
      pay: 'saas:keruyun:pay',
      unfreeze: 'saas:keruyun:unfreeze',
      cancelOrder: 'saas:keruyun:cancelOrder'
    }

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

 const hasPermission = (code) => permissionCodes.value.includes(code)

    /**
   * 是否有任一资金操作权限（决定操作区是否展示）。
     * 注意：这里只看「有没有资格」，具体某个按钮能不能点
     * 还要看后端返回的 canPay / canUnfreeze（业务状态决定）。
     */
    const hasOpPermission = computed(() =>
      hasPermission(PERM.pay)
    || hasPermission(PERM.unfreeze)
      || hasPermission(PERM.cancelOrder)
    )

    const loading = ref(false)
    const list = ref([])
 const query = reactive({ creditStatus: '', rentalStatus: '', keyword: '' })

    // 当前 Tab（租赁单状态）。'' 表示「全部」
    const activeTab = ref('')

    // 各状态数量，由后端 statusCount 返回（与 Tab 过滤无关，反映全量分布）
    const statusCount = ref({})

    // Tab 定义：顺序按业务流转，排最前的是运营最常处理的
    const STATUS_TABS = [
      { value: '', label: '全部' },
      { value: '待上门安装', label: '待上门安装' },
      { value: '履约中', label: '履约中' },
      { value: '待支付押金', label: '待免押确认' },
      { value: '已完成', label: '已完成' },
      { value: '已关闭', label: '已关闭' }
    ]

    const statusTabs = computed(() =>
      STATUS_TABS.map(t => ({ ...t, badge: t.value ? (statusCount.value[t.value] || 0) : 0 }))
    )
    const pagination = reactive({ total: 0, pageNum: 1, pageSize: 20 })

    // 详情
    const detailVisible = ref(false)
    const detailLoading = ref(false)
    const detail = ref(null)

    // 扣款
    const payVisible = ref(false)
    const paySubmitting = ref(false)
    const payMode = ref('full')
    const payRow = ref(null)
    const payForm = reactive({ amount: 0.01, remark: '' })

    // 取消扣款
    const unfreezeVisible = ref(false)
    const unfreezeSubmitting = ref(false)
    const unfreezeRow = ref(null)
    const unfreezeForm = reactive({ remark: '' })

    // 取消免押单
    const cancelVisible = ref(false)
    const cancelSubmitting = ref(false)
    const cancelRow = ref(null)
    const cancelForm = reactive({ remark: '' })

  // ============ 已扣款交易的后续操作：关闭交易 / 退款（老辛 2026-10-10）============
    const closeTradeVisible = ref(false)
  const closeTradeSubmitting = ref(false)
    const closeTradeRow = ref(null)
    const closeTradeForm = reactive({ remark: '' })

    const refundVisible = ref(false)
    const refundSubmitting = ref(false)
    const refundRow = ref(null)
    const refundForm = reactive({ amount: 0.01, remark: '' })

    const columns = [
      { prop: 'rentalNo', label: '业务单号', width: 180, type: 'mono' },
      { prop: 'storeName', label: '门店', width: 150 },
      { prop: 'userName', label: '用户', width: 200 },
      { prop: 'creditStatus', label: '免押状态', width: 130 },
      { prop: 'money', label: '免押额度 / 已扣', width: 150 },
      { prop: 'rentalStatus', label: '租赁单', width: 110 }
    ]

    const fetchList = async () => {
      loading.value = true
      try {
        const r = await saasChannelService.listCreditOrders({
          creditStatus: query.creditStatus,
          rentalStatus: query.rentalStatus,
    keyword: query.keyword,
        pageNum: pagination.pageNum,
          pageSize: pagination.pageSize
        })
        const d = r && r.data ? r.data : {}
     list.value = d.list || []
        pagination.total = d.total || 0
        // 状态数量由后端统计，即使当前 Tab 已过滤也会返回全量分布
        if (d.statusCount) {
    statusCount.value = d.statusCount
        }
        // 后端在「该渠道无门店映射」时会返回 hint，运营一眼知道不是系统故障
        if (d.hint) {
  ElMessage.warning(d.hint)
        }
      } catch (e) {
        ElMessage.error('加载失败：' + ((e && e.msg) || e))
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.pageNum = 1
      fetchList()
    }

    // 切换 Tab：同步到 query 并回到第一页
    const handleTabChange = (name) => {
 query.rentalStatus = name || ''
      pagination.pageNum = 1
      fetchList()
    }

    const handleReset = () => {
      query.rentalStatus = ''
    activeTab.value = ''
      query.creditStatus = ''
      query.keyword = ''
      pagination.pageNum = 1
      fetchList()
    }

    const handleCurrentChange = (p) => {
      pagination.pageNum = p
      fetchList()
    }

    const handleSizeChange = (s) => {
      pagination.pageSize = s
      pagination.pageNum = 1
      fetchList()
    }

    // ============ 详情 ============
    const openDetail = async (row) => {
      detailVisible.value = true
      detailLoading.value = true
      detail.value = null
      try {
        const r = await saasChannelService.getCreditOrderDetail(row.rentalNo)
        detail.value = r && r.data ? r.data : null
      } catch (e) {
        ElMessage.error('加载详情失败：' + ((e && e.msg) || e))
      } finally {
        detailLoading.value = false
      }
    }

    // ============ 扣款 ============
    const openPay = (row, mode) => {
      payRow.value = row
      payMode.value = mode
      payForm.amount = mode === 'full' ? Number(row.restAmount) : 0.01
      payForm.remark = ''
      payVisible.value = true
    }

    const submitPay = async () => {
      if (!payForm.remark || !payForm.remark.trim()) {
        ElMessage.warning('请填写扣款原因')
        return
      }
      const row = payRow.value
      const amount = payMode.value === 'full' ? Number(row.restAmount) : Number(payForm.amount)
      // 二次确认：金额较大时避免手滑
      try {
        await ElMessageBox.confirm(
          `确认为单号 ${row.rentalNo} 扣款 ¥${amount}？此操作不可撤销。`,
          '扣款确认',
          { type: 'warning', confirmButtonText: '确认扣款', cancelButtonText: '再想想' }
        )
      } catch (e) {
        return
      }
      paySubmitting.value = true
      try {
        const payload = { rentalNo: row.rentalNo, remark: payForm.remark.trim() }
        if (payMode.value === 'partial') {
          payload.amount = payForm.amount
        }
        const r = payMode.value === 'full'
          ? await saasChannelService.payFull(payload)
          : await saasChannelService.payPartial(payload)
        if (r && r.code === 200) {
          ElMessage.success('扣款成功')
          payVisible.value = false
          fetchList()
        } else {
          ElMessage.error((r && r.msg) || '扣款失败')
        }
      } catch (e) {
        ElMessage.error('扣款失败：' + ((e && e.msg) || e))
      } finally {
        paySubmitting.value = false
      }
    }

    // ============ 取消扣款 ============
    const openUnfreeze = (row) => {
      unfreezeRow.value = row
      unfreezeForm.remark = ''
      unfreezeVisible.value = true
    }

    const submitUnfreeze = async () => {
      if (!unfreezeForm.remark.trim()) {
        ElMessage.warning('请填写取消原因')
        return
      }
      unfreezeSubmitting.value = true
      try {
        const r = await saasChannelService.unfreeze({
          rentalNo: unfreezeRow.value.rentalNo,
          remark: unfreezeForm.remark.trim()
        })
        if (r && r.code === 200) {
          ElMessage.success('已取消扣款，资金已解冻')
          unfreezeVisible.value = false
          fetchList()
        } else {
          ElMessage.error((r && r.msg) || '操作失败')
        }
      } catch (e) {
        ElMessage.error('操作失败：' + ((e && e.msg) || e))
      } finally {
        unfreezeSubmitting.value = false
      }
    }

    // ============ 取消免押单 ============
    // ============ 关闭交易 / 退款（老辛 2026-10-10）============
    /** 是否是扣款类流水（只有扣款才有 close/refund，退款/解冻流水不适用） */
    const isPayOp = (opType) => opType === 'PAY_FULL' || opType === 'PARTIAL_PAY'

    /** 打开退款弹窗：先问后端能不能退、可退多少 */
    const openRefund = async (row) => {
      try {
    const r = await saasChannelService.checkOpLog(row.id)
        const d = (r && r.data) || {}
     if (!d.canRefund) {
          ElMessage.warning(d.reason || '该笔暂不可退款')
          return
        }
        refundRow.value = row
      refundForm.amount = Number(d.refundableAmount || 0)
        refundForm.remark = ''
   refundVisible.value = true
      } catch (e) {
        ElMessage.error((e && e.msg) || '查询可退金额失败')
      }
    }

    const submitRefund = async () => {
      if (!refundForm.remark || !refundForm.remark.trim()) {
     ElMessage.warning('请填写退款原因')
        return
      }
      const row = refundRow.value
      const amount = Number(refundForm.amount || 0)
      try {
        await ElMessageBox.confirm(
          `确认为流水 ${row.id} 退款 ¥${amount}？退款原因会展示在用户支付宝账单里，且不可撤销。`,
          '退款确认',
   { type: 'warning', confirmButtonText: '确认退款', cancelButtonText: '再想想' }
        )
  } catch (e) {
        return
      }
      refundSubmitting.value = true
      try {
     const r = await saasChannelService.refund({
      opLogId: row.id,
          amount,
          remark: refundForm.remark.trim()
    })
        if (r && r.code === 200) {
   ElMessage.success(r.msg || '退款成功')
          refundVisible.value = false
    openDetail(detail.value)
        } else {
          ElMessage.error((r && r.msg) || '退款失败')
        }
      } catch (e) {
        ElMessage.error('退款失败：' + ((e && e.msg) || e))
      } finally {
  refundSubmitting.value = false
      }
    }

  /** 打开关闭交易弹窗 */
    const openCloseTrade = (row) => {
      closeTradeRow.value = row
   closeTradeForm.remark = ''
      closeTradeVisible.value = true
    }

    const submitCloseTrade = async () => {
      if (!closeTradeForm.remark || !closeTradeForm.remark.trim()) {
        ElMessage.warning('请填写关闭原因')
        return
      }
      const row = closeTradeRow.value
  try {
        await ElMessageBox.confirm(
          `确认关闭交易 ${row.tradeNo || row.outTradeNo}？关闭后该交易不可再支付。`,
          '关闭交易确认',
  { type: 'warning', confirmButtonText: '确认关闭', cancelButtonText: '再想想' }
        )
   } catch (e) {
        return
      }
      closeTradeSubmitting.value = true
      try {
        const r = await saasChannelService.closeTrade({
          opLogId: row.id,
          remark: closeTradeForm.remark.trim()
   })
        if (r && r.code === 200) {
       ElMessage.success(r.msg || '交易已关闭')
          closeTradeVisible.value = false
          openDetail(detail.value)
        } else {
          ElMessage.error((r && r.msg) || '关闭失败')
        }
      } catch (e) {
        ElMessage.error('关闭失败：' + ((e && e.msg) || e))
   } finally {
   closeTradeSubmitting.value = false
      }
    }

    const openCancel = (row) => {
      cancelRow.value = row
      cancelForm.remark = ''
      cancelVisible.value = true
    }

    const submitCancel = async () => {
      if (!cancelForm.remark.trim()) {
        ElMessage.warning('请填写取消原因')
        return
      }
      cancelSubmitting.value = true
      try {
        const r = await saasChannelService.cancelOrder({
          rentalNo: cancelRow.value.rentalNo,
          remark: cancelForm.remark.trim()
        })
        if (r && r.code === 200) {
          ElMessage.success('免押单已取消')
          cancelVisible.value = false
          fetchList()
        } else {
          ElMessage.error((r && r.msg) || '操作失败')
        }
      } catch (e) {
        ElMessage.error('操作失败：' + ((e && e.msg) || e))
      } finally {
        cancelSubmitting.value = false
      }
    }

    // ============ 展示辅助 ============
    const creditStatusText = (s) => {
      if (s === 'PASSED') return '已免押'
      if (s === 'FAILED') return '未通过'
      if (s === 'PENDING') return '评估中'
      return s || '-'
    }
    const creditStatusClass = (s) => {
      if (s === 'PASSED') return 'st-done'
      if (s === 'FAILED') return 'st-warn'
      return 'st-info'
    }
    const failReasonText = (r) => {
      if (r === 'REJECTED') return '芝麻拒绝'
      if (r === 'ABANDONED') return '用户放弃'
      if (r === 'TIMEOUT') return '超时关闭'
      return ''
    }
    const rentalStatusClass = (s) => {
      if (s === '已完成') return 'st-done'
      if (s === '已关闭') return 'st-warn'
      return 'st-info'
    }
    const formatDate = (t) => {
      if (!t) return '-'
      return String(t).replace('T', ' ').substring(0, 19)
    }

    onMounted(fetchList)

    return {
      loading, list, query, pagination, columns,
      activeTab, statusTabs, statusCount, handleTabChange,
      PERM, hasPermission, hasOpPermission,
      detailVisible, detailLoading, detail,
      payVisible, paySubmitting, payMode, payRow, payForm,
      unfreezeVisible, unfreezeSubmitting, unfreezeRow, unfreezeForm,
      cancelVisible, cancelSubmitting, cancelRow, cancelForm,
  closeTradeVisible, closeTradeSubmitting, closeTradeRow, closeTradeForm,
    refundVisible, refundSubmitting, refundRow, refundForm,
      fetchList, handleSearch, handleReset, handleCurrentChange, handleSizeChange,
      openDetail, openPay, submitPay, openUnfreeze, submitUnfreeze,
      openCancel, submitCancel, isPayOp, openCloseTrade, submitCloseTrade, openRefund, submitRefund,
            creditStatusText, creditStatusClass, failReasonText, rentalStatusClass, formatDate
    }
  }
})
</script>

<style scoped>
/* ===== 状态 Tab ===== */
.status-tabs {
  margin-bottom: 4px;
}
.status-tabs :deep(.el-tabs__header) {
  margin-bottom: 12px;
}
.tab-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.tab-badge {
  display: inline-block;
  min-width: 18px;
  padding: 0 5px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 12px;
  border-radius: 9px;
  background: #e8eef7;
  color: #1a73e8;
}
.status-tabs :deep(.el-tabs__item.is-active) .tab-badge {
  background: #1a73e8;
  color: #fff;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.filter-bar__spacer {
  flex: 1;
}
.sub-tag {
  margin-left: 6px;
  font-size: 12px;
  color: #909399;
}
.user-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}
.user-cell__name {
  color: #303133;
}
.user-cell__uid {
  font-size: 12px;
  color: #909399;
  font-family: Menlo, Consolas, monospace;
}
.money-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}
.money-cell__rest {
  font-weight: 600;
  color: #1a73e8;
}
.money-cell__sub {
  font-size: 12px;
  color: #909399;
}
.money {
  font-weight: 600;
  color: #1a73e8;
}
.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
}
.detail__ops {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 14px;
  margin: 14px 0 4px;
  background: #f7f9fc;
  border: 1px solid #e4e9f2;
  border-radius: 6px;
}
.detail__ops-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  margin-right: 4px;
}
.detail__ops-empty {
  font-size: 12px;
  color: #909399;
}
.dt-none {
  color: #c0c4cc;
}

.detail__section-title {
  margin: 18px 0 10px;
  font-weight: 600;
  color: #303133;
}
.pay-alert {
  margin-bottom: 16px;
}
.pay-form :deep(.el-form-item) {
  margin-bottom: 16px;
}
</style>
